import { memo, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  Position,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Play, MonitorSmartphone, GitBranch, Variable, RefreshCw,
  DatabaseZap, Database, Trash2, Zap, Workflow, Clock, CircleAlert, Box,
  type LucideIcon,
} from 'lucide-react';
import { flowXmlToGraph } from '@/utils/flowXmlToGraph';

const KIND: Record<string, { label: string; icon: LucideIcon; color: string }> = {
  start: { label: 'Start', icon: Play, color: 'text-ma-ok-text bg-ma-ok-soft' },
  screen: { label: 'Screen', icon: MonitorSmartphone, color: 'text-ma-info-text bg-ma-info-soft' },
  decision: { label: 'Decision', icon: GitBranch, color: 'text-ma-warn-text bg-ma-warn-soft' },
  assignment: { label: 'Assignment', icon: Variable, color: 'text-purple-600 bg-purple-500/10' },
  loop: { label: 'Loop', icon: RefreshCw, color: 'text-indigo-600 bg-indigo-500/10' },
  recordCreate: { label: 'Create Records', icon: DatabaseZap, color: 'text-ma-ok-text bg-ma-ok-soft' },
  recordUpdate: { label: 'Update Records', icon: DatabaseZap, color: 'text-ma-ok-text bg-ma-ok-soft' },
  recordDelete: { label: 'Delete Records', icon: Trash2, color: 'text-ma-err-text bg-ma-err-soft' },
  recordLookup: { label: 'Get Records', icon: Database, color: 'text-teal-600 bg-teal-500/10' },
  action: { label: 'Action', icon: Zap, color: 'text-cyan-600 bg-cyan-500/10' },
  subflow: { label: 'Subflow', icon: Workflow, color: 'text-violet-600 bg-violet-500/10' },
  wait: { label: 'Pause / Wait', icon: Clock, color: 'text-ma-warn-text bg-ma-warn-soft' },
  error: { label: 'Custom Error', icon: CircleAlert, color: 'text-ma-err-text bg-ma-err-soft' },
  transform: { label: 'Transform', icon: Box, color: 'text-slate-600 bg-slate-500/10' },
};

const FlowElementNode = memo(({ data }: NodeProps) => {
  const d = data as { kind: string; label: string };
  const meta = KIND[d.kind] || { label: d.kind, icon: Box, color: 'text-slate-600 bg-slate-500/10' };
  const Icon = meta.icon;
  return (
    <div className="px-3 py-2 rounded-lg border-2 border-border bg-background shadow-sm min-w-[170px] max-w-[230px]">
      <Handle type="target" position={Position.Top} className="!h-2 !w-2 !bg-muted-foreground/40 !border-0" />
      <div className="flex items-center gap-2">
        <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md ${meta.color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-semibold text-foreground" title={d.label}>{d.label}</div>
          <div className="text-[11px] text-muted-foreground">{meta.label}</div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!h-2 !w-2 !bg-muted-foreground/40 !border-0" />
    </div>
  );
});
FlowElementNode.displayName = 'FlowElementNode';

const nodeTypes = { flowElement: FlowElementNode };

/** Render a Salesforce Flow's `.flow-meta.xml` as a read-only Flow-Builder-style
 *  diagram. Returns null if the content isn't a parseable Flow (caller falls back
 *  to the raw XML view). */
export function FlowDiagramView({ xml }: { xml: string }) {
  const graph = useMemo(() => flowXmlToGraph(xml), [xml]);
  if (!graph || !graph.nodes.length) {
    return (
      <div className="p-4 text-xs text-muted-foreground">
        Couldn&apos;t render this as a flow diagram — showing the source instead.
      </div>
    );
  }
  return (
    <div className="h-[480px] w-full overflow-hidden rounded-md border border-border bg-muted/20">
      <ReactFlow
        nodes={graph.nodes}
        edges={graph.edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        proOptions={{ hideAttribution: true }}
        minZoom={0.2}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}

/** True when an artifact looks like a Salesforce Flow we can diagram. */
export function looksLikeFlow(opts: { type?: string; name?: string; content?: string }): boolean {
  const t = (opts.type || '').toLowerCase();
  const n = (opts.name || '').toLowerCase();
  const c = opts.content || '';
  if (n.endsWith('.flow') || n.endsWith('.flow-meta.xml')) return true;
  if (t.includes('flow')) return true;
  return c.indexOf('<Flow') !== -1 && c.indexOf('</Flow>') !== -1;
}
